import React, { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Badge from '../Badge/Badge';
import data, { socialLinks } from '../../Utils/data';
import './Hero.css';

gsap.registerPlugin(ScrollTrigger);

/* The card in the lower right of the frame. The original is a video
   thumbnail; there is no video, so the slot carries a real shipped
   product instead of a play button that would go nowhere. */
const FEATURED = data.selectedWork.find((p) => p.cover && p.link);

function Hero({ personal, standalone = false }) {
  const rootRef = useRef(null);
  const { fullName, handle } = personal;

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.matchMedia().add('(prefers-reduced-motion: no-preference)', () => {
        const tl = gsap.timeline({ defaults: { ease: 'power3.out' } })
          .from('.hero__eyebrow', { opacity: 0, x: -16, duration: 0.7 }, 0.1)
          .from('.hero__line', { yPercent: 108, duration: 0.95, stagger: 0.09 }, 0.15)
          .from('.hero__block', { scaleY: 0, transformOrigin: 'bottom center', duration: 0.9 }, 0.3)
          .from('.hero__portrait', { opacity: 0, y: 26, duration: 0.9 }, 0.45)
          .from('.hero__handle', { opacity: 0, duration: 0.6 }, 0.8)
          .from('.hero__intro > *', { opacity: 0, y: 18, duration: 0.75, stagger: 0.09 }, 0.5)
          .from('.hero__badge', { opacity: 0, scale: 0.85, duration: 0.8 }, 0.25)
          .from('.hero__card', { opacity: 0, y: 22, duration: 0.8 }, 0.7);

        /* Bars wipe out from the left as they enter. The relative lengths
           are the point of the component, so they should be seen drawing. */
        const bars = gsap.from('.bar__fill', {
          scrollTrigger: { trigger: '.hero__bars', start: 'top 92%' },
          scaleX: 0,
          transformOrigin: 'left center',
          duration: 0.9,
          stagger: 0.09,
          ease: 'power3.out',
        });

        return () => { tl.kill(); bars.kill(); };
      });
    }, rootRef);

    return () => ctx.revert();
  }, []);

  return (
    <>
      {standalone && (
        <Helmet>
          <title>{fullName}, Software Engineer</title>
          <meta
            name="description"
            content="Software engineer building scalable systems and fluid interfaces with React, Node.js and React Native."
          />
        </Helmet>
      )}

      <section className="hero" ref={rootRef} id="top">
        <div className="hero__grid shell">

          <div className="hero__badge">
            <Badge text="Dream big. Work hard. Ship it." />
          </div>

          <div className="hero__headline">
            <span className="eyebrow hero__eyebrow">Hi there</span>
            <h1 className="hero__title">
              <span className="clip"><span className="hero__line">lets think</span></span>
              <span className="clip"><span className="hero__line accent">creative</span></span>
            </h1>
          </div>

          <figure className="hero__figure">
            <span className="hero__block" aria-hidden="true" />
            <img
              className="hero__portrait"
              src="/assets/img/profile.png"
              alt={`Portrait of ${fullName}`}
              width="394"
              height="298"
            />
            <figcaption>
              <a
                className="hero__handle ul-link"
                href={socialLinks.github}
                target="_blank"
                rel="noreferrer"
              >
                {handle}
              </a>
            </figcaption>
          </figure>

          <div className="hero__intro">
            <h2 className="hero__name">
              I&apos;m <span className="ul-link">{fullName}</span>
            </h2>
            <p>
              A software engineer who builds scalable systems and the interfaces
              that sit on top of them. Six years across fintech, edtech, SaaS
              and mobile, shipping products used by hundreds of thousands.
            </p>
            <Link to="/about" className="arrow-link">
              Learn More
              <ArrowRight size={17} strokeWidth={2} aria-hidden="true" />
            </Link>
          </div>

          <ul className="hero__bars">
            {data.about.stats.map((s) => (
              <li className="bar" key={s.label}>
                <span
                  className={`bar__fill bar__fill--${s.tone}`}
                  style={{ '--w': s.weight }}
                >
                  <span className="bar__label">
                    {s.value} {s.label}
                  </span>
                </span>
              </li>
            ))}
          </ul>

          {FEATURED && (
            <a
              className="hero__card"
              href={FEATURED.link}
              target="_blank"
              rel="noreferrer"
            >
              <span className="hero__card-media">
                <img src={FEATURED.cover} alt={`${FEATURED.title} interface`} loading="lazy" />
              </span>
              <span className="hero__card-body">
                <span className="hero__card-title">{FEATURED.title}</span>
                <span className="hero__card-sub">{FEATURED.category}</span>
                <span className="arrow-link">
                  Learn More
                  <ArrowRight size={16} strokeWidth={2} aria-hidden="true" />
                </span>
              </span>
            </a>
          )}

        </div>
      </section>
    </>
  );
}

export default Hero;
